import React, { useState } from 'react';
import { Box, Typography, Avatar, Button, Skeleton } from '@mui/material';
import { CameraAlt, Logout } from '@mui/icons-material';
import { useAuth } from '../../hooks/AuthProvider';
import { GlassmorphicCard } from '../GlassmorphicCard/GlassmorphicCard';
import { AuthAnalytics } from '../../lib/analytics';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import AvatarUploadModal from './AvatarUploadModal';
import { GoogleJwtPayload } from 'src/types/interfaces';

interface ProfileSidebarProps {
  className?: string;
}

const ProfileSidebar: React.FC<ProfileSidebarProps> = ({ className }) => {
  const { userProfile, isLoading, updateProfile } = useAuth();
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // Get user data from profile with fallback to JWT token (same logic as Header)
  const getUserData = () => {
    const displayName = userProfile?.firstName && userProfile?.lastName
      ? `${userProfile.firstName} ${userProfile.lastName}`.trim()
      : userProfile?.firstName || userProfile?.lastName || 'User';

    // Get avatar image with same fallback logic as Header
    const getAvatarImage = () => {
      // First try to get avatar from userProfile (backend)
      if (userProfile?.person?.avatarUrl) {
        return userProfile.person.avatarUrl;
      }

      // Fallback to JWT token picture
      try {
        const token = Cookies.get("social_at");
        if (token) {
          const decoded = jwtDecode<GoogleJwtPayload>(token);
          return decoded.picture || '/default-avatar.png';
        }
      } catch (error) {
        console.error('Error decoding token:', error);
      }

      return '/default-avatar.png';
    };

    return {
      name: displayName,
      email: userProfile?.email || '',
      picture: getAvatarImage()
    };
  };

  const userData = getUserData();

  const auth = useAuth();

  const handleSignOut = () => {
    AuthAnalytics.logout();
    auth.logOut();
  };

  const handlePhotoUpload = async (file: File) => {
    setIsUploading(true);
    try {
      // Convert file to base64 for simple upload
      const reader = new FileReader();
      const base64Promise = new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const base64Data = await base64Promise;

      // Update profile with new avatar
      if (!userProfile?.person?.id) {
        throw new Error('User profile not loaded');
      }

      await updateProfile({
        person: {
          ...userProfile.person,
          avatarUrl: base64Data,
        }
      });

      setUploadModalOpen(false);
    } catch (error) {
      console.error('Photo upload failed:', error);
      throw new Error('Failed to upload photo. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  // Show loading skeleton when profile is loading
  if (isLoading && !userProfile) {
    return (
      <GlassmorphicCard className={`profile-sidebar ${className || ''}`}>
        <Box p={3} display="flex" flexDirection="column" alignItems="center" height="100%">
          <Box position="relative" mb={2}>
            <Skeleton variant="circular" width={80} height={80} />
          </Box>
          <Skeleton variant="text" width={120} height={32} sx={{ mb: 1 }} />
          <Skeleton variant="text" width={150} height={20} sx={{ mb: 3 }} />
          <Box flexGrow={1} />
          <Skeleton variant="rectangular" width="100%" height={40} />
        </Box>
      </GlassmorphicCard>
    );
  }

  return (
    <GlassmorphicCard className={`profile-sidebar ${className || ''}`}>
      <Box p={3} display="flex" flexDirection="column" alignItems="center" height="100%">
        {/* Avatar Section */}
        <Box position="relative" mb={2}>
          <Avatar
            src={userData.picture || undefined}
            alt={userData.name}
            sx={{
              width: 80,
              height: 80,
              border: '3px solid transparent',
              background: 'linear-gradient(135deg, #f06ef5, #9c88ff)',
              backgroundClip: 'padding-box',
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                padding: '3px',
                background: 'linear-gradient(135deg, #f06ef5, #9c88ff)',
                borderRadius: 'inherit',
                mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                maskComposite: 'exclude',
                zIndex: -1,
              }
            }}
          />

          {/* Camera overlay - click to upload */}
          <Box
            position="absolute"
            bottom={-4}
            right={-4}
            onClick={() => setUploadModalOpen(true)}
            sx={{
              background: 'linear-gradient(135deg, #f06ef5, #9c88ff)',
              borderRadius: '50%',
              width: 28,
              height: 28,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              opacity: 1,
              transition: 'all 0.2s ease-in-out',
              boxShadow: '0 4px 12px rgba(240, 110, 245, 0.3)',
              '&:hover': {
                transform: 'scale(1.1)',
                boxShadow: '0 6px 16px rgba(240, 110, 245, 0.4)',
              },
              '&:active': {
                transform: 'scale(0.95)',
              },
            }}
          >
            <CameraAlt sx={{ fontSize: 14, color: 'white' }} />
          </Box>
        </Box>

        {/* User Info */}
        <Typography variant="h6" textAlign="center" mb={1} fontWeight={600}>
          {userData.name}
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          textAlign="center"
          mb={3}
          sx={{ wordBreak: 'break-word' }}
        >
          {userData.email}
        </Typography>

        {/* Spacer to push sign out button to bottom */}
        <Box flexGrow={1} />

        {/* Sign Out Button */}
        <Button
          variant="outlined"
          color="error"
          startIcon={<Logout />}
          onClick={handleSignOut}
          fullWidth
          sx={{
            borderColor: 'error.main',
            color: 'error.main',
            '&:hover': {
              borderColor: 'error.dark',
              backgroundColor: 'error.main',
              color: 'white',
              transform: 'translateY(-2px)',
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
          Sign Out
        </Button>

        {/* Avatar Upload Modal */}
        <AvatarUploadModal
          open={uploadModalOpen}
          onClose={() => setUploadModalOpen(false)}
          currentAvatar={userData.picture}
          onUpload={handlePhotoUpload}
          isUploading={isUploading}
        />
      </Box>
    </GlassmorphicCard>
  );
};

export default ProfileSidebar;